import { useId } from 'react';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { Hero } from '@xing-com/typography';

import * as Styled from './promoted-card.styled';
import type { PromotedCardProps } from './promoted-card.types';
import {
  CardActionGroup,
  CardBenefitsList,
  CardCallout,
  CardContainer,
  CardDiscoverButton,
  CardHeaderSection,
  CardPricing,
} from './sub-components';

export type { PromotedCardProps } from './promoted-card.types';

export const PromotedCard = ({
  title,
  benefitsTitle,
  benefits,
  price,
  previousPrice,
  action,
  activationUrl,
  cancellation,
  detailsUrl,
  discover,
  onDiscover,
  perks,
  callout,
  socialProof,
}: PromotedCardProps): ReactElement => {
  const calloutId = useId();
  const { formatMessage } = useIntl();
  const hasTextualCallout = callout.variant !== 'default';
  const recommendedLabel = formatMessage({
    id: 'UPSELL_MEMBERSHIPS_PRODUCT_CARD_RECOMMENDED',
  });

  return (
    <CardContainer
      aria-describedby={hasTextualCallout ? calloutId : undefined}
      aria-label={`${title}, ${recommendedLabel}`}
      as="article"
      size="large"
      padding="regular"
      selected
    >
      <CardCallout callout={callout} id={calloutId} />
      <CardHeaderSection>
        <Hero as="h2" size="small" noMargin>
          {title}
        </Hero>
        <CardPricing price={price} previousPrice={previousPrice} />
        <CardActionGroup
          action={action}
          activationUrl={activationUrl}
          cancellation={cancellation}
          promoted
        />
      </CardHeaderSection>
      <Styled.CardBenefitsSection>
        <CardBenefitsList
          benefits={benefits}
          benefitsTitle={benefitsTitle}
          socialProof={socialProof}
          promoted
        />
        <CardDiscoverButton
          action={action}
          activationUrl={activationUrl}
          cancellation={cancellation}
          detailsUrl={detailsUrl}
          discover={discover}
          onDiscover={onDiscover}
          perks={perks}
          previousPrice={previousPrice}
          price={price}
          title={title}
        />
      </Styled.CardBenefitsSection>
    </CardContainer>
  );
};
